import {FC, useEffect, useState} from 'react';
import {Box, Button, Center, Flex, Stack} from '@chakra-ui/react';
import {api, ESummaryStatus, formatRelativeTime, useT} from '@kcast-fe/core';
import {Controller, useForm} from 'react-hook-form';
import {Input, Loader, Switch, Textarea, ConfirmDialog, useScreenSize} from 'ui-kit';

interface IProps {
  id: string;
  onDelete: () => void;
}

interface IContentForm {
  title: string;
  text: string;
}

const SummaryContentTabFC: FC<IProps> = ({id, onDelete}) => {
  const {data: content, isLoading: isContentLoading} = api.summary.useSummaryContentQuery(id);
  const {data: settings} = api.summary.useSummaryPromptSettingsQuery(id);
  const {data: state} = api.summary.useSummaryStateQuery(id);

  const {t} = useT();

  const updateContentMutation = api.summary.useUpdateSummaryContentMutation();
  const updateStateMutation = api.summary.useUpdateSummaryStateMutation();
  const deleteMutation = api.summary.useDeleteSummaryMutation();
  const replayMutation = api.summary.useReplaySummaryMutation();
  const cancelMutation = api.summary.useCancelReplaySummaryMutation();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReplay, setConfirmReplay] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const {register, control, handleSubmit, reset, setValue, formState} = useForm<IContentForm>({
    defaultValues: {title: '', text: ''}
  });

  const {isLessThan768} = useScreenSize();

  useEffect(() => {
    if (content && !formState.dirtyFields.title) {
      setValue('title', content.title, {shouldDirty: false});
    }
    if (content && !formState.dirtyFields.text) {
      setValue('text', content.text, {shouldDirty: false});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const isReplaying = state?.status === ESummaryStatus.Replaying;

  const onSubmit = (data: IContentForm) => {
    updateContentMutation.mutate(
      {id, data: {title: data.title.trim(), text: data.text}},
      {onSuccess: () => reset({title: data.title.trim(), text: data.text})}
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(id, {onSuccess: onDelete});
  };

  const handleReplay = () => {
    replayMutation.mutate(id, {onSuccess: () => setConfirmReplay(false)});
  };

  const handleCancel = () => {
    cancelMutation.mutate(id, {onSuccess: () => setConfirmCancel(false)});
  };

  if (isContentLoading) {
    return (
      <Center h={'100px'}>
        <Loader size={'lg'} />
      </Center>
    );
  }

  return (
    <>
      <Stack h={'100%'} gap={'0'}>
        <Box flex={'1 1 auto'} overflowY={'auto'} p={'4px 0 16px'}>
          <Stack gap={'16px'}>
            <Box>
              <Box color={'{colors.textSecondary}'} mb={'6px'}>
                {t('summary.fields.title')}
              </Box>
              <Input
                size={'lg'}
                disabled={isReplaying}
                {...register('title', {required: true, validate: (v) => !!v.trim()})}
              />
            </Box>

            <Box>
              <Box color={'{colors.textSecondary}'} mb={'6px'}>
                {t('summary.fields.content')}
              </Box>
              <Controller
                name={'text'}
                control={control}
                render={({field}) => (
                  <Textarea
                    rows={10}
                    size={'lg'}
                    value={field.value}
                    autoresize={false}
                    limit={settings?.char_limit ?? 0}
                    disabled={isReplaying}
                    onChange={field.onChange}
                  />
                )}
              />
            </Box>

            {content?.updated_at && (
              <Box color={'{colors.textTertiary}'}>
                {t('summary.fields.lastUpdated')} {formatRelativeTime(content.updated_at)}
              </Box>
            )}

            <Box>
              <Flex alignItems={'center'} gap={'8px'}>
                <Switch
                  checked={state?.enabled ?? false}
                  onChange={(checked) => updateStateMutation.mutate({id, data: {enabled: checked}})}
                  disabled={updateStateMutation.isPending}
                />
                <Box color={'{colors.textSecondary}'}>
                  {state?.enabled
                    ? t('summary.state.enabledLabel')
                    : t('summary.state.disabledLabel')}
                </Box>
              </Flex>
            </Box>
          </Stack>
        </Box>

        <Flex
          pt={'24px'}
          justifyContent={'space-between'}
          alignItems={'center'}
          borderTop={'thin solid'}
          borderColor={'{colors.bgMain}'}
        >
          <Button
            size={isLessThan768 ? 'md' : 'lg'}
            /* @ts-ignore */
            variant={'danger'}
            disabled={isReplaying || updateContentMutation.isPending}
            onClick={() => setConfirmDelete(true)}
          >
            {t('actions.delete')}
          </Button>

          <Flex alignItems={'center'} justifyContent={'flex-end'}>
            <Flex gap={'8px'}>
              {isReplaying ? (
                <Button
                  size={isLessThan768 ? 'md' : 'lg'}
                  variant={'subtle'}
                  onClick={() => setConfirmCancel(true)}
                >
                  {t('summary.actions.cancelReplay')}
                </Button>
              ) : (
                <Button
                  size={isLessThan768 ? 'md' : 'lg'}
                  variant={'subtle'}
                  onClick={() => setConfirmReplay(true)}
                >
                  {t('summary.actions.replay')}
                </Button>
              )}
              <Button
                size={isLessThan768 ? 'md' : 'lg'}
                variant={'solid'}
                disabled={isReplaying || updateContentMutation.isPending}
                onClick={handleSubmit(onSubmit)}
              >
                {t('actions.save')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Stack>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={t('summary.confirm.deleteTitle')}
        body={t('summary.confirm.deleteBody')}
        confirmLabel={t('actions.delete')}
        variant={'danger'}
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmDialog
        isOpen={confirmReplay}
        title={t('summary.confirm.replayTitle')}
        body={t('summary.confirm.replayBody')}
        confirmLabel={t('summary.actions.replay')}
        isLoading={replayMutation.isPending}
        onConfirm={handleReplay}
        onCancel={() => setConfirmReplay(false)}
      />

      <ConfirmDialog
        isOpen={confirmCancel}
        title={t('summary.confirm.cancelReplayTitle')}
        body={t('summary.confirm.cancelReplayBody')}
        confirmLabel={t('summary.actions.cancelReplay')}
        isLoading={cancelMutation.isPending}
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancel(false)}
      />
    </>
  );
};

export const SummaryContentTab = SummaryContentTabFC;
